import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

function ResetPasswordPage() {
  const { token } = useParams(); // Get token from URL
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = loading, true/false = result
  const [passwordStrength, setPasswordStrength] = useState(null); // Password strength feedback

  // Validate token on page load
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`/api/auth/validate-reset-token/${token}`);
        const data = await response.json();
        setTokenValid(data.valid);
        if (!data.valid) {
          setError('This password reset link has expired or is invalid. Please request a new one.');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setTokenValid(false);
        setError('An error occurred while validating the reset link.');
      }
    };
    validateToken();
  }, [token]);

  // Check password strength as user types
  const checkPasswordStrength = useCallback(async (pwd) => {
    if (!pwd) {
      setPasswordStrength(null);
      return;
    }

    try {
      const response = await fetch('/api/auth/check-password-strength', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd })
      });

      if (!response.ok) {
        console.error('Password strength check failed:', response.status);
        setPasswordStrength(null);
        return;
      }

      const data = await response.json();
      setPasswordStrength(data);
    } catch (err) {
      console.error('Error checking password strength:', err);
      setPasswordStrength(null);
    }
  }, []);

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    checkPasswordStrength(pwd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message + ' You can now log in with your new password.');
        // Optionally redirect to login page after a short delay
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(data.message || 'Failed to reset password.');
      }
    } catch (err) {
      console.error('Reset password request error:', err);
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while validating token
  if (tokenValid === null) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4 sm:p-6">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200 w-full max-w-sm">
          <h2 className="text-3xl font-bold text-primary-700 mb-6 text-center">Reset Password</h2>
          <div className="flex justify-center">
            <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-center text-gray-600 mt-4">Validating reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4 sm:p-6">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200 w-full max-w-sm">
        <h2 className="text-3xl font-bold text-primary-700 mb-6 text-center">Reset Password</h2>

        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{message}</span>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" disabled={!tokenValid || error}>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="••••••••"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              required
              disabled={!tokenValid}
            />

            {/* Password Strength Meter */}
            {passwordStrength && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Password Strength</span>
                  <span className={`text-xs font-semibold ${
                    passwordStrength.score === 0 ? 'text-red-600' :
                    passwordStrength.score === 1 ? 'text-orange-600' :
                    passwordStrength.score === 2 ? 'text-yellow-600' :
                    passwordStrength.score === 3 ? 'text-blue-600' :
                    'text-green-600'
                  }`}>
                    {passwordStrength.score === 0 ? 'Very Weak' :
                     passwordStrength.score === 1 ? 'Weak' :
                     passwordStrength.score === 2 ? 'Fair' :
                     passwordStrength.score === 3 ? 'Good' :
                     'Strong'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      passwordStrength.score === 0 ? 'bg-red-600 w-1/5' :
                      passwordStrength.score === 1 ? 'bg-orange-600 w-2/5' :
                      passwordStrength.score === 2 ? 'bg-yellow-600 w-3/5' :
                      passwordStrength.score === 3 ? 'bg-blue-600 w-4/5' :
                      'bg-green-600 w-full'
                    }`}
                  />
                </div>
                {passwordStrength.feedback.warning && (
                  <p className="mt-2 text-xs text-gray-600">{passwordStrength.feedback.warning}</p>
                )}
                {passwordStrength.feedback.suggestions && passwordStrength.feedback.suggestions.length > 0 && (
                  <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                    {passwordStrength.feedback.suggestions.map((suggestion, idx) => (
                      <li key={idx}>{suggestion}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              required
            />
          </div>
          
          <button
            type="submit"
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-300"
            disabled={loading}
          >
            {loading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-gray-600 text-sm">
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
